import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { campusService, authService } from '../api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Building2 } from 'lucide-react';
import ListPageHeader from '../components/list/ListPageHeader';
import ListToolbar from '../components/list/ListToolbar';
import ListPagination from '../components/list/ListPagination';
import ListEmptyState from '../components/list/ListEmptyState';
import ListLoadingState from '../components/list/ListLoadingState';
import { ShortCardGrid, ListSection } from '../components/list/ShortCard';
import { CampusShortCard } from '../components/list/ShortCardVariants';

function SelectCampus() {
    const [campuses, setCampuses] = useState([]);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const { refreshUser } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        campusService.getCampuses()
            .then((res) => {
                const active = (res.data.results || res.data).filter((c) => c.is_active);
                setCampuses(active);
            })
            .catch(() => toast.error('Failed to load campuses'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        setPage(1);
    }, [search]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return campuses.filter((c) => c.name.toLowerCase().includes(q));
    }, [search, campuses]);

    const paged = useMemo(
        () => filtered.slice((page - 1) * pageSize, page * pageSize),
        [filtered, page, pageSize]
    );

    const handleSubmit = async () => {
        if (!selected) return toast.error('Please select a campus');
        setSubmitting(true);
        try {
            await authService.switchCampus(selected);
            await refreshUser();
            toast.success('Campus selected!');
            navigate('/dashboard');
        } catch (err) {
            toast.error('Failed to select campus');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: 'var(--color-bg-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', color: 'var(--color-text-primary)' }}>
            <div style={{ width: '100%', maxWidth: '900px' }} className="ux-list-page">
                <ListPageHeader
                    kicker="Onboarding"
                    kickerIcon={Building2}
                    title="Select Your Campus"
                    description="Choose the organisation you'll learn with."
                />

                <div style={{ background: 'var(--color-card-bg)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: '1.25rem', border: '1px solid var(--color-border)' }}>
                    <ListToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search campuses…" />

                    {loading ? (
                        <ListLoadingState label="Loading campuses…" />
                    ) : filtered.length === 0 ? (
                        <ListEmptyState icon={Building2} title="No campuses found" description="Try a different search." />
                    ) : (
                        <>
                            <ListSection title="Available campuses">
                                <ShortCardGrid>
                                    {paged.map((campus, index) => (
                                        <CampusShortCard
                                            key={campus.id}
                                            campus={campus}
                                            index={index}
                                            selected={selected === campus.id}
                                            onClick={() => setSelected(campus.id)}
                                        />
                                    ))}
                                </ShortCardGrid>
                            </ListSection>
                            <ListPagination
                                page={page}
                                pageSize={pageSize}
                                totalCount={filtered.length}
                                onPageChange={setPage}
                                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                                pageSizes={[8, 12, 24]}
                            />
                        </>
                    )}

                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!selected || submitting}
                        className="btn btn-primary w-full"
                        style={{ marginTop: '1.25rem', padding: '14px', fontSize: '1rem', opacity: (!selected || submitting) ? 0.6 : 1 }}
                    >
                        {submitting ? 'Confirming…' : 'Continue to Dashboard'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default SelectCampus;
